import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { ref, uploadBytes } from "firebase/storage";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { storage } from "@/integrations/firebase/storage";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/utils/toast";
import { logError } from "@/shared/services/ErrorReporting";
import { useAnalytics } from "@/hooks/useAnalytics";
import { getContentType } from "@/shared/constants";

export interface IFileUploadContext {
  isUploading: boolean;
  uploadFile: (desiredWordCount: number, outputLanguage: string) => void;
  setFile: (file: File) => void;
  file: File | undefined;
}

export const FileUploadContext = createContext<IFileUploadContext>(
  undefined as never,
);

export const useFileUpload = () => useContext(FileUploadContext);

export const FileUploadProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(["home"]);
  const { authUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const { logEvent } = useAnalytics();
  const [file, setFile] = useState<File>();

  const uploadFile = useCallback(
    async (desiredWordCount: number, outputLanguage: string) => {
      if (!authUser || !file) {
        return;
      }

      const createJobRequest = (jobId: string, manuscriptUri: string) => {
        const jobRequestRef = doc(db, "job_requests", jobId);
        return setDoc(jobRequestRef, {
          user_id: authUser!.uid,
          job_id: jobId,
          manuscript_uri: manuscriptUri,
          status: "PENDING_UPLOAD",
          uploaded_at: serverTimestamp(),
          desired_word_count: desiredWordCount,
          output_language: outputLanguage,
        });
      };

      setIsUploading(true);
      const toastId = toast.loading(t("home:fileUpload.toast.loading"), {
        duration: Infinity,
      });

      const jobId = uuidv4();
      const storagePath = `${authUser.uid}/${jobId}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      const bucket = (storage.app.options as { storageBucket?: string })
        .storageBucket;
      const gcsUri = `gs://${bucket}/${storagePath}`;

      const contentType = getContentType(file);

      try {
        await createJobRequest(jobId, gcsUri);
        await uploadBytes(storageRef, file, { contentType });
        toast.success(t("home:fileUpload.toast.success"), {
          id: toastId,
        });
        logEvent("summary_job_requested", {
          desiredWordCount,
          fileType: contentType,
          fileSize: file.size,
          outputLanguage,
        });
      } catch (error) {
        logError(error);
        toast.error(t("home:fileUpload.toast.errorGeneric"), {
          id: toastId,
          duration: Infinity,
        });
      } finally {
        setFile(undefined);
        setIsUploading(false);
      }
    },
    [authUser, file, t, logEvent],
  );

  const value = useMemo(
    () => ({
      isUploading,
      setFile,
      uploadFile,
      file,
    }),
    [isUploading, file, uploadFile],
  );

  return (
    <FileUploadContext.Provider value={value}>
      {children}
    </FileUploadContext.Provider>
  );
};
